import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";

import useOnClickOutside from "utils/hooks/useOnClickOutside";
import useHover from "utils/hooks/useHover";
import { sortTop } from "utils/fetchMapInfo";

import {
    StyledPrefix,
    ContainerSelectedValue,
    ItemLi,
    PopupSelect,
} from "../MultiSelect/styles";
import { ItemsMultiSelectSearch } from "./styles";
import { ArrowDown } from "components/PopupMenu/styles";
import { StyledBlock } from "../containers";
import { Label } from "../generic";
import { StrNum, ISelectItems } from "interfaces/intarfaces";

export interface ISearchSelect extends React.HTMLAttributes<HTMLSelectElement> {
    title?: string;
    items?: ISelectItems[];
    showItems?: ISelectItems[];
    topValues?: string[];
    placeholder?: string;
    changeItem: (x: StrNum) => void;
    value?: StrNum;
    w?: string;
    grow?: string;
    p?: string;
    id?: string;
    disabled?: boolean;
    required?: boolean;
    handleLeave?: any
    handleClick?: any
}

const SearchSelect: React.FC<ISearchSelect> = ({
    title,
    items,
    showItems,
    topValues = [],
    value,
    placeholder,
    changeItem,
    w = "100%",
    grow = "0",
    p,
    id,
    required = false,
    disabled,
    handleLeave,
    handleClick,
}) => {

    const ref = useRef(null);
    const inputRef = useRef<HTMLInputElement>(null)

    const [show, setShow] = useState(false);
    const [showItemsDisplay, setShowItems] = useState<ISelectItems[] | undefined>(items);

    const [hoverRef, isHovered] = useHover<HTMLDivElement>();

    useOnClickOutside(ref, () => {
        !isHovered && setShow(false)
        inputRef.current?.blur()
        updateInputValue()
    });

    const handleEnter = (event: React.KeyboardEvent<HTMLInputElement>) => {
        event.preventDefault();
        let listItems: ISelectItems[] = []
        if (showItemsDisplay) {
            listItems = sortTop(showItemsDisplay, topValues)
        }
        if (listItems[0]) {
            value = listItems[0].value
            changeItem(value)
        }
        setShow(false)
        inputRef.current?.blur()
        updateInputValue()
    }

    const updateInputValue = () => {
        if (inputRef.current) {
            inputRef.current.value = getLabel()
        }
    }

    const handleInput = (event: React.ChangeEvent<HTMLInputElement>, focus: boolean = false) => {
        changeItem('')
        let inputText = event.target.value;

        showItems = [];
        if (inputText && !focus) {
            items?.forEach(function (item) {
                if (item.label.toLocaleLowerCase().includes(inputText.toLocaleLowerCase())) {
                    showItems?.push(item)
                }
            });
        } else {
            showItems = items;
        }
        setShowItems(showItems);
    };

    const getLabel = () => {
        let label = "";
        const item = items?.filter((item) => item.value === value);
        if (item && item?.length > 0) label = item[0].label;
        return label;
    };

    const { i18n } = useTranslation()

    useEffect(() => {
        handleClick && changeItem(i18n.language)
        handleClick && handleClick(i18n.language)
    }, [handleClick, i18n.language])

    if (value) {
        updateInputValue();
    };

    return (
        <StyledBlock p={p} id={id}>
            {title && (
                <Label p="0" bgColor="transparet" fontWeight="600">
                    {title}
                </Label>
            )}
            <StyledBlock
                onClick={() => {
                    !disabled && setShow(!show);
                    inputRef.current?.focus()
                }}
                ref={hoverRef}
            >
                <StyledPrefix>
                    <ArrowDown size="2em" up={show} />
                </StyledPrefix>
                <ContainerSelectedValue error={required && !value}>
                    <ItemsMultiSelectSearch
                        placeholder={placeholder}
                        ref={inputRef}
                        onChange={event => { handleInput(event) }}
                        onFocus={event => { event.target.value = ''; handleInput(event, true); }}
                        onKeyDown={event => {
                            if (event.key === 'Enter') { handleEnter(event) }
                            else if (event.key === 'Tab') { setShow(false); updateInputValue(); };
                        }
                        }
                    />
                </ContainerSelectedValue>
            </StyledBlock>
            {show && (
                <PopupSelect className={show ? "panel active" : "panel"} ref={ref}>
                    {showItemsDisplay &&
                        sortTop(showItemsDisplay, topValues).map((item) => (
                            <ItemLi
                                key={item.value}
                                onClick={() => {
                                    handleLeave && handleLeave();
                                    handleClick && handleClick(item.value);
                                    changeItem(item.value)
                                    updateInputValue();
                                    setShow(false);
                                }}
                            >
                                {item.label}
                            </ItemLi>
                        ))
                    }
                </PopupSelect>
            )}
        </StyledBlock>
    );
};


export default SearchSelect;
export interface ISearchSelectHook {
    changeItem: React.Dispatch<React.SetStateAction<StrNum | undefined>>;
    value: StrNum | undefined;
}

export const useSearchSelect = (initial?: StrNum | undefined, language?: boolean): ISearchSelectHook => {
    const [value, setValue] = useState<StrNum | undefined>(initial);
    // const { i18n } = useTranslation()

    return {
        changeItem: setValue,
        value
    };
};